import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { api } from './services/api';
import { useAuthStore } from './stores/authStore';
import './styles/globals.css';
import './styles/theme-neon-tokens.css';
import './styles/theme-platform.css';
// NOTE: the legacy `theme-trading-agent.css` was removed — tokens for the
// Trading Agent terminal theme now live in `shared/platform/theme/tokens.css`
// under `body.theme-trading-agent[data-mode="dark|light"]`, which matches
// the `tmp/platform-mockups/trading-agent.html` green-on-black aesthetic.
// 015-chat-widget-polish: shared platform theme tokens (6 apps × 2 modes).
// MUST import AFTER the legacy theme CSS so the new per-app tokens win the
// cascade for equal-specificity selectors (body.theme-{app}[data-mode=...]).
import '@shared/platform/theme/tokens.css';
// Tokens bridge — per-app overrides of Tailwind's shadcn/ui HSL variables
// (--background, --foreground, --card, --primary, --border, etc.) so
// components using `bg-background`/`text-foreground`/`text-primary` etc.
// visibly reflect the active `body.theme-{app}[data-mode]` theme.
import '@shared/platform/theme/tokens-bridge.css';
import '@shared/platform/tabs/tabs.css';
import '@shared/chat/widget/widget.css';
// Launcher-specific layout styles (hero + widget cards). Must load AFTER
// shared tokens so they can reference `var(--fg)`/`var(--bg)`/etc.
import './styles/launcher-home.css';
import './styles/launcher-widgets.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

// Attach the JWT to every API request straight from the auth store. Reading
// the token live at request time (instead of via a useEffect in App that runs
// after child effects) closes the race where ProtectedRoute's /auth/me fired
// before the token was attached and bounced the user back to /login.
api.setTokenProvider(() => useAuthStore.getState().accessToken);

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found');

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>
);

// Register push notification service worker (T117)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/push-sw.js').catch(() => {
    // Push SW registration failed — push notifications unavailable
  });
}
